import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Tag, InputNumber, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getRolePageList, getRoleDetail, addRole, updateRole, deleteRole, updateRoleStatus } from '../../api/role';
import './index.css';
import dayjs from 'dayjs';

const { Option } = Select as any;
const { TextArea } = Input;

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchParams, setSearchParams] = useState({
    roleName: '',
    tenantId: null,
    roleType: null,
    isSystem: null,
    isEnabled: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [form] = Form.useForm();

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '租户ID',
      dataIndex: 'tenantId',
      key: 'tenantId',
      width: 100,
    },
    {
      title: '角色名称',
      dataIndex: 'roleName',
      key: 'roleName',
      width: 150,
    },
    {
      title: '角色类型',
      dataIndex: 'roleType',
      key: 'roleType',
      width: 120,
      render: (type) => {
        const typeMap = {
          0: '普通角色',
          1: '管理员',
          2: '超级管理员',
        };
        return typeMap[type] || '未知';
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '系统角色',
      dataIndex: 'isSystem',
      key: 'isSystem',
      width: 100,
      render: (isSystem) => (
        <Tag color={isSystem === 1 ? 'red' : 'default'}>
          {isSystem === 1 ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (isEnabled) => (
        <Tag color={isEnabled === 1 ? 'green' : 'red'}>
          {isEnabled === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 180,
      render: (text) => {
        if (!text) return '-';
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleToggleStatus(record)}
          >
            {record.isEnabled === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定要删除这个角色吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 获取角色列表
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const params = {
        PageIndex: pagination.current,
        PageSize: pagination.pageSize,
        ...searchParams,
      };
      
      const response: any = await getRolePageList(params);
      
      if (response?.res_status_code === '0' && response?.res_content) {
        setRoles(response.res_content.items || []);
        const rawTotal =
          response?.res_content?.totalCount ??
          response?.res_content?.TotalCount ??
          response?.totalCount ??
          response?.TotalCount ??
          response?.res_content?.total ??
          response?.res_content?.Total ??
          0;
        const total = Number(rawTotal) || 0;
        setPagination({
          ...pagination,
          total,
        });
      } else {
        message.error(response?.res_message || '获取角色列表失败');
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
      message.error('获取角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [pagination.current, pagination.pageSize]);

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchRoles();
  };

  // 重置搜索
  const handleReset = () => {
    setSearchParams({
      roleName: '',
      tenantId: null,
      roleType: null,
      isSystem: null,
      isEnabled: null,
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => {
      fetchRoles();
    }, 100);
  };

  // 新增角色
  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑角色
  const handleEdit = async (record) => {
    try {
      const response: any = await getRoleDetail(record.id);
      if (response?.res_status_code === '0' && response?.res_content) {
        setEditingRole(response.res_content);
        form.setFieldsValue({
          ...response.res_content,
          isSystem: response.res_content.isSystem === 1,
          isEnabled: response.res_content.isEnabled === 1,
        });
        setModalVisible(true);
      } else {
        message.error(response?.res_message || '获取角色详情失败');
      }
    } catch (error) {
      console.error('获取角色详情失败:', error);
      message.error('获取角色详情失败');
    }
  };

  // 保存角色
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        isSystem: values.isSystem ? 1 : 0,
        isEnabled: values.isEnabled ? 1 : 0,
      };

      if (editingRole) {
        // 更新
        data.Id = editingRole.id;
        const response: any = await updateRole(data);
        if (response?.res_status_code === '0') {
          message.success('更新角色成功');
          setModalVisible(false);
          fetchRoles();
        } else {
          message.error(response?.res_message || '更新角色失败');
        }
      } else {
        // 新增
        const response: any = await addRole(data);
        if (response?.res_status_code === '0') {
          message.success('添加角色成功');
          setModalVisible(false);
          fetchRoles();
        } else {
          message.error(response?.res_message || '添加角色失败');
        }
      }
    } catch (error) {
      console.error('保存角色失败:', error);
      if (error.errorFields) {
        return; // 表单验证错误
      }
      message.error('保存角色失败');
    }
  };

  // 删除角色
  const handleDelete = async (id) => {
    try {
      const response: any = await deleteRole({ Ids: [id] });
      if (response?.res_status_code === '0') {
        message.success('删除角色成功');
        fetchRoles();
      } else {
        message.error(response?.res_message || '删除角色失败');
      }
    } catch (error) {
      console.error('删除角色失败:', error);
      message.error('删除角色失败');
    }
  };

  // 切换状态
  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.isEnabled === 1 ? 0 : 1;
      const response: any = await updateRoleStatus({
        IsEnabled: newStatus,
        Ids: [record.id],
      });
      if (response?.res_status_code === '0') {
        message.success(`${newStatus === 1 ? '启用' : '禁用'}角色成功`);
        fetchRoles();
      } else {
        message.error(response?.res_message || '更新状态失败');
      }
    } catch (error) {
      console.error('更新状态失败:', error);
      message.error('更新状态失败');
    }
  };

  // 分页变化
  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current:
        newPagination.pageSize && newPagination.pageSize !== prev.pageSize
          ? 1
          : newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  return (
    <div className="roles-page">
      <div className="page-header">
        <h2>角色管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增角色
        </Button>
      </div>

      {/* 搜索区域 */}
      <div className="search-container">
        <Space size="middle" wrap>
          <Input
            placeholder="角色名称"
            value={searchParams.roleName}
            onChange={(e) => setSearchParams({ ...searchParams, roleName: e.target.value })}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="角色类型"
            value={searchParams.roleType}
            onChange={(value) => setSearchParams({ ...searchParams, roleType: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value={0}>普通角色</Option>
            <Option value={1}>管理员</Option>
            <Option value={2}>超级管理员</Option>
          </Select>
          <Select
            placeholder="是否系统角色"
            value={searchParams.isSystem}
            onChange={(value) => setSearchParams({ ...searchParams, isSystem: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value={1}>是</Option>
            <Option value={0}>否</Option>
          </Select>
          <Select
            placeholder="状态"
            value={searchParams.isEnabled}
            onChange={(value) => setSearchParams({ ...searchParams, isEnabled: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value={1}>启用</Option>
            <Option value={0}>禁用</Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={roles}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          size: 'small',
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200, y: 'calc(100vh - 320px)' }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingRole ? '编辑角色' : '新增角色'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="horizontal"
          labelAlign="right"
          colon={false}
          labelCol={{ flex: '110px' }}
          wrapperCol={{ flex: 1 }}
        >
          <Form.Item
            name="tenantId"
            label="租户ID"
          >
            <InputNumber style={{ width: '100%' }} placeholder="留空表示全局角色" />
          </Form.Item>
          <Form.Item
            name="roleName"
            label="角色名称"
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder="请输入角色名称" />
          </Form.Item>
          <Form.Item
            name="roleType"
            label="角色类型"
          >
            <Select>
              <Option value={0}>普通角色</Option>
              <Option value={1}>管理员</Option>
              <Option value={2}>超级管理员</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>
          <Form.Item
            name="isSystem"
            label="系统角色"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="isEnabled"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Roles;
